import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Switch, Select, InputNumber, Row, Col, Button, Space, message, Modal } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { createWorkOrderTemplate, getWorkOrderTemplateById, updateWorkOrderTemplate } from '../../../../services/workorders';

const { TextArea } = Input;
const { Option } = Select;

const TemplateEditPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showItemSchemaHelp, setShowItemSchemaHelp] = useState(false);
  const [showChecklistHelp, setShowChecklistHelp] = useState(false);
  const [showAttachHelp, setShowAttachHelp] = useState(false);
  const [showPrintHelp, setShowPrintHelp] = useState(false);
  const [showAssignHelp, setShowAssignHelp] = useState(false);
  const [showSplitHelp, setShowSplitHelp] = useState(false);
  const [showDeviceHelp, setShowDeviceHelp] = useState(false);
  const [showMetaHelp, setShowMetaHelp] = useState(false);
  const [showPriorityHelp, setShowPriorityHelp] = useState(false);
  const [showSLAHelp, setShowSLAHelp] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id ? String(params.id) : undefined;
  const search = typeof window !== 'undefined' ? (window as any).location?.search : '';
  const categoryFromUrl = React.useMemo(() => {
    try { const sp = new URLSearchParams(search || ''); return (sp.get('category') || 'customer') as 'customer'|'internal'; } catch { return 'customer'; }
  }, [search]);

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getWorkOrderTemplateById(id);
      form.setFieldsValue(data);
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(()=>{ load(); }, [id]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setLoading(true);
      if (id) await updateWorkOrderTemplate(id, v);
      else await createWorkOrderTemplate(v);
      message.success('已保存');
      navigate('/dashboard/admin/work-orders/templates');
    } catch (e:any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="list-page-container">
      <Card loading={loading}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{id ? '编辑工单模板' : '新增工单模板'}</div>
          <Space>
            <Button onClick={() => navigate(-1)}>返回</Button>
            <Button type="primary" onClick={submit}>保存</Button>
          </Space>
        </div>
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            {!id ? (
              <Col span={8}>
                <Form.Item name="category" label="模板域" initialValue={categoryFromUrl}>
                  <Select>
                    <Option value="customer">客户</Option>
                    <Option value="internal">仓内运维</Option>
                  </Select>
                </Form.Item>
              </Col>
            ) : null}
            <Col span={8}>
              <Form.Item name="code" label="编码" rules={[{ required: true }]}>
                <Input placeholder="如 RE-LABEL/PICK" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="name" label="名称" rules={[{ required: true }]}>
                <Input placeholder="模板名称" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="operation_type" label="操作类型">
                <Input placeholder="如 relabel/pick/putaway" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="default_priority"
                label="默认优先级"
                extra={<Button type="link" size="small" onClick={() => setShowPriorityHelp(true)}>查看说明</Button>}
              >
                <Select allowClear>
                  <Option value="normal">普通</Option>
                  <Option value="high">一般优先</Option>
                  <Option value="super">超级优先</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="default_sla_minutes"
                label="默认SLA(分钟)"
                extra={<Button type="link" size="small" onClick={() => setShowSLAHelp(true)}>查看说明</Button>}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="item_schema"
                label="项字段Schema(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowItemSchemaHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'{"planned_qty":{"type":"number"}, "uom":{"type":"string"}}'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="checklist_schema"
                label="步骤清单Schema(JSON Array)"
                extra={<Button type="link" size="small" onClick={() => setShowChecklistHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'[{"key":"print_label","label":"打印新标签","required":true}]'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="attachment_requirements"
                label="附件需求(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowAttachHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'{"order":{"required":[{"label":"新标签PDF","ext":"pdf"}]}}'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="print_profiles"
                label="打印配置(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowPrintHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'{"label":{"template":"A6","copies":1}}'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="auto_assign_rule"
                label="自动分派规则(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowAssignHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'{"role":"warehouse_staff","strategy":"least_workload"}'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="auto_split_rule"
                label="自动拆单规则(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowSplitHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={6} placeholder={'{"split_by":"instruction_item","max_items_per_order":200}'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                name="device_hints"
                label="设备提示(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowDeviceHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={4} placeholder={'{"scanner":{"required":true},"printer":{"model":"ZPL"}}'} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item
                name="meta"
                label="Meta(JSON)"
                extra={<Button type="link" size="small" onClick={() => setShowMetaHelp(true)}>查看示例</Button>}
              >
                <TextArea rows={4} placeholder={'{"billing":{"unit_price":0.12,"uom":"EA"}}'} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      {/* 示例弹窗 */}
      <Modal title="默认优先级 说明" open={showPriorityHelp} onCancel={() => setShowPriorityHelp(false)} footer={null} width={640}>
        <div style={{ color: '#666' }}>
          用于新工单的初始优先级（影响调度/排序/看板提醒）。可被路由 overrides 或人工在工单上覆盖，不会锁死。
          建议：normal（默认）、high（较紧急）、super（紧急）。
        </div>
      </Modal>
      <Modal title="默认SLA(分钟) 说明" open={showSLAHelp} onCancel={() => setShowSLAHelp(false)} footer={null} width={640}>
        <div style={{ color: '#666' }}>
          生成工单时用于计算截止时间（如 due_at = 受理/开始时间 + SLA），用于超时预警与报表统计。实际执行中可被覆盖或调整。
          建议配置常用档（如 120/240/480 分钟）。
        </div>
      </Modal>
      <Modal title="项字段Schema 示例" open={showItemSchemaHelp} onCancel={() => setShowItemSchemaHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义工单项的自定义字段与校验提示（键值 → 字段定义）。支持：type、label、required、enum、min/max等。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "planned_qty": { "type": "number", "label": "预计数量", "min": 0 },
  "uom": { "type": "string", "label": "单位", "enum": ["EA","BOX"] },
  "remark": { "type": "string", "label": "备注" }
}`}
        </pre>
      </Modal>
      <Modal title="步骤清单Schema 示例" open={showChecklistHelp} onCancel={() => setShowChecklistHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义本模板的操作步骤（顺序执行）。每步：key、label、required，可附加 meta。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`[
  { "key": "print_label", "label": "打印新标签", "required": true },
  { "key": "attach_label", "label": "粘贴标签" },
  { "key": "pack", "label": "装箱" }
]`}
        </pre>
      </Modal>
      <Modal title="附件需求 示例" open={showAttachHelp} onCancel={() => setShowAttachHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义必须/可选的附件要求；按作用域（order/item）区分；可按 label/ext/mime_type 标识。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "order": {
    "required": [
      { "label": "新旧条码对应表", "ext": "xlsx" },
      { "label": "新标签PDF", "ext": "pdf" }
    ]
  }
}`}
        </pre>
      </Modal>
      <Modal title="打印配置 示例" open={showPrintHelp} onCancel={() => setShowPrintHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义打印模板与份数等，例如标签/箱标配置；执行步骤可引用对应 key。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "label": { "template": "A6", "copies": 1 },
  "box_label": { "template": "A4", "copies": 1 }
}`}
        </pre>
      </Modal>
      <Modal title="自动分派规则 示例" open={showAssignHelp} onCancel={() => setShowAssignHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义工单分派策略与目标群组/角色等。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "role": "warehouse_staff",
  "group": "pack",
  "strategy": "least_workload"
}`}
        </pre>
      </Modal>
      <Modal title="自动拆单规则 示例" open={showSplitHelp} onCancel={() => setShowSplitHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>定义拆单策略（按仓库/按对象分组等）与每单容量上限等。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "split_by": "instruction_item",
  "max_items_per_order": 200
}`}
        </pre>
      </Modal>
      <Modal title="设备提示 示例" open={showDeviceHelp} onCancel={() => setShowDeviceHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>指示执行需要的设备/外设提示，如扫码枪/打印机/秤等。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "scanner": { "required": true },
  "printer": { "model": "ZPL" },
  "scale": { "required": false }
}`}
        </pre>
      </Modal>
      <Modal title="Meta 示例" open={showMetaHelp} onCancel={() => setShowMetaHelp(false)} footer={null} width={720}>
        <div style={{ color: '#666', marginBottom: 8 }}>其他扩展信息（固化到模板快照）。建议在此设置默认计费单价。</div>
        <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12 }}>
{`{
  "billing": { "unit_price": 0.12, "uom": "EA" },
  "ui": { "show_item_remark": true }
}`}
        </pre>
      </Modal>
    </div>
  );
};

export default TemplateEditPage;


